'use server'

import { apiRequest, ApiResponse } from '../request'
import type { ServiceLabels } from '@/lib/form-state/util'

export type PaymentType = 'PRIVATE' | 'INSURANCE' | 'MIXED'

export type ServicePaymentStatus =
    | 'PAYMENT_PENDING'
    | 'PAYMENT_AWAITING_AUTHORIZATION'
    | 'PAYMENT_AUTHORIZED'
    | 'PAYMENT_BILLED'
    | 'PAYMENT_COMPLETED'
    | 'PAYMENT_CANCELLED'

export interface ServicePayment {
    id: number
    companyId: number
    clientId: number
    client?: {
        id: number
        name: string
    }
    serviceType: string
    serviceId: number
    healthPlanId?: number
    healthPlan?: {
        id: number
        name: string
    }
    guideId?: number
    guide?: {
        id: number
        guideNumber: string
    }
    authorizationId?: number
    authorization?: {
        id: number
        authorizationNumber: string
    }
    paymentId?: string
    paymentType?: PaymentType
    status: ServicePaymentStatus
    cardNumber?: string
    totalValue: number
    insuranceValue?: number
    privateValue?: number
    coparticipationValue?: number
    discount?: number
    finalValue: number
    paidAt?: string
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface CreateServicePaymentData {
    clientId: number
    serviceType: string
    serviceId: number
    totalValue: number
    notes?: string
}

export interface UpdateServicePaymentData {
    paymentType?: PaymentType
    healthPlanId?: number
    cardNumber?: string
    insuranceValue?: number
    privateValue?: number
    coparticipationValue?: number
    discount?: number
    notes?: string
}

export interface SetPaymentTypeData {
    paymentType: PaymentType
    healthPlanId?: number
    cardNumber?: string
    insuranceValue?: number
    privateValue?: number
    coparticipationValue?: number
}

export interface ServicePaymentFilters {
    clientId?: number
    serviceType?: string
    paymentType?: PaymentType
    status?: ServicePaymentStatus
    healthPlanId?: number
    startDate?: string
    endDate?: string
}

export interface ServicePaymentStats {
    totalServices: number
    totalValue: number
    totalInsurance: number
    totalPrivate: number
    totalCoparticipation: number
    byType: Record<PaymentType, number>
    byStatus: Record<ServicePaymentStatus, number>
}

// Service Payment CRUD
export async function getServicePayments(
    filters?: ServicePaymentFilters
): Promise<ApiResponse<ServicePayment[]>> {
    return apiRequest<ServicePayment[]>('billing/service-payments', {
        params: filters,
    })
}

export async function getServicePayment(
    id: number
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(`billing/service-payments/${id}`, {})
}

export async function createServicePayment(
    data: CreateServicePaymentData
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>('billing/service-payments', { data }, 'post')
}

export async function updateServicePayment(
    id: number,
    data: UpdateServicePaymentData
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}`,
        { data },
        'patch'
    )
}

// Get by service
export async function getServicePaymentByService(
    serviceType: string,
    serviceId: number
): Promise<ApiResponse<ServicePayment | null>> {
    return apiRequest<ServicePayment | null>(
        `billing/service-payments/service/${serviceType}/${serviceId}`,
        {}
    )
}

// Set payment type
export async function setPaymentType(
    id: number,
    data: SetPaymentTypeData
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/set-payment-type`,
        { data },
        'post'
    )
}

// Link guide
export async function linkGuide(
    id: number,
    guideId: number
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/link-guide`,
        { data: { guideId } },
        'post'
    )
}

// Link authorization
export async function linkAuthorization(
    id: number,
    authorizationId: number
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/link-authorization`,
        { data: { authorizationId } },
        'post'
    )
}

// Link payment
export async function linkPayment(
    id: number,
    paymentId: string
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/link-payment`,
        { data: { paymentId } },
        'post'
    )
}

// Mark as billed to insurance
export async function markAsBilledToInsurance(
    id: number
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/bill-insurance`,
        {},
        'post'
    )
}

// Cancel
export async function cancelServicePayment(
    id: number
): Promise<ApiResponse<ServicePayment>> {
    return apiRequest<ServicePayment>(
        `billing/service-payments/${id}/cancel`,
        {},
        'post'
    )
}

// Statistics
export async function getServicePaymentStats(filters?: {
    startDate?: string
    endDate?: string
}): Promise<ApiResponse<ServicePaymentStats>> {
    return apiRequest<ServicePaymentStats>('billing/service-payments/statistics', {
        params: filters,
    })
}

// Labels
export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
    PRIVATE: 'Particular',
    INSURANCE: 'Convenio',
    MIXED: 'Misto',
}

export const PAYMENT_TYPE_COLORS: Record<PaymentType, string> = {
    PRIVATE: 'badge-secondary',
    INSURANCE: 'badge-secondary',
    MIXED: 'badge-accent',
}

export const SERVICE_PAYMENT_STATUS_LABELS: Record<
    ServicePaymentStatus,
    string
> = {
    PAYMENT_PENDING: 'Pendente',
    PAYMENT_AWAITING_AUTHORIZATION: 'Aguardando Autorizacao',
    PAYMENT_AUTHORIZED: 'Autorizado',
    PAYMENT_BILLED: 'Faturado',
    PAYMENT_COMPLETED: 'Concluido',
    PAYMENT_CANCELLED: 'Cancelado',
}

export const SERVICE_PAYMENT_STATUS_COLORS: Record<
    ServicePaymentStatus,
    string
> = {
    PAYMENT_PENDING: 'badge-warning',
    PAYMENT_AWAITING_AUTHORIZATION: 'badge-info',
    PAYMENT_AUTHORIZED: 'badge-success',
    PAYMENT_BILLED: 'badge-info',
    PAYMENT_COMPLETED: 'badge-success',
    PAYMENT_CANCELLED: 'badge-ghost',
}

export const SERVICE_TYPE_LABELS: Record<string, string> = {
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
    HOSPITALIZATION: 'Internação',
}

/** `SERVICE_TYPE_LABELS` no vocabulário do segmento. */
export function getServiceTypeLabels(
    labels: ServiceLabels
): Record<string, string> {
    return {
        CONSULTATION: labels.Consultation,
        EXAM: labels.Exam,
        PROCEDURE: labels.Order,
        HOSPITALIZATION: 'Internação',
    }
}
